import {Button, Skeleton, Stack, Typography} from '@mui/material';
import {RiFolderUploadFill} from 'react-icons/ri';
import {translations} from '../../../i18n/translations';
import {StorageFileType} from '../../../backend/enums';
import {useTranslation} from 'react-i18next';
import {IoDocumentTextOutline} from 'react-icons/io5';
import './add-attachments-form.scss';
import {FiTrash2} from 'react-icons/fi';
import {useEffect, useState} from 'react';
import {DailyCareLogModal} from '../handle-task-form/daily-care-modal';
import {Attachment, AttachmentDataFile} from '../../../backend/interfaces/attachment';
import {BackendApiManager} from '../../../managers/backend-api';
import {AttachmentType} from '../../../backend/enums/attachment-type';
import {acceptedTypes, getStorageAndAttachmentTypes} from './attachments-utils';

const fileSizeMbLimit = 10;
const fileSizeLimit = fileSizeMbLimit * 1024 * 1024;

export type SavingAttachment = {
  attachmentType: AttachmentType;
  storageType: StorageFileType;
  file: File;
  storageUrl?: string;
};

type AddAttachmentsFormProps = {
  type: 'upload' | 'view';
  isDailyCareLogApp?: boolean;
  attachments?: SavingAttachment[];
  attachmentsUrls?: Attachment[];
  onError: (errors: AttachmentError[]) => void;
  onAttachmentsChange: (attachments: SavingAttachment[]) => void;
};

type AttachmentError = {
  file: File;
  error: string;
};

export const AddAttachmentsForm = ({
  type,
  isDailyCareLogApp,
  attachments,
  attachmentsUrls,
  onAttachmentsChange,
  onError,
}: AddAttachmentsFormProps) => {
  const {t} = useTranslation();
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<SavingAttachment>();
  const [files, setFiles] = useState<SavingAttachment[]>();
  const containerStyle = {
    background: isDailyCareLogApp ? 'var(--dcm-white-base)' : 'var(--dcm-bgseniormenu)',
    boxShadow: isDailyCareLogApp ? '0px 4px 16px 0px rgba(29, 41, 57, 0.1) ' : 'none',
  };
  const [filesErrors, setFileErrors] = useState<AttachmentError[]>([]);

  useEffect(() => {
    const convertFileUrlToFile = async (
      fileUrl: string,
      fileName: string
    ): Promise<SavingAttachment | null> => {
      try {
        const response = await BackendApiManager.instance.get(fileUrl, {
          responseType: 'blob',
        });
        const blob = response.data as Blob;
        const file = new File([blob], fileName, {type: blob.type});

        const types = getStorageAndAttachmentTypes(file.type);
        if (types === null) {
          return null;
        }

        return {
          attachmentType: types.attachmentType,
          storageType: types.storageType,
          file: file,
          storageUrl: fileUrl,
        };
      } catch (error) {
        console.error('Error converting file URL to File:', error);
        return null;
      }
    };

    const convertUrlToFiles = async (attachments: Attachment[]) => {
      const files: SavingAttachment[] = [];

      attachments.forEach(async (attachment) => {
        const data = attachment.data as AttachmentDataFile;
        const pathname = new URL(data.fileUrl).pathname;
        const name = attachment.name ?? pathname.substring(pathname.lastIndexOf('/') + 1);
        const file = await convertFileUrlToFile(data.fileUrl, name);

        if (file) {
          files.push(file);
          setFiles((prevFiles) => {
            return prevFiles ? [...prevFiles, file] : [file];
          });
        } else {
          setFiles((prevFiles) => {
            return prevFiles ? [...prevFiles] : [];
          });
        }
      });
    };

    if (type === 'view') {
      if (attachmentsUrls && attachmentsUrls.length > 0) {
        convertUrlToFiles(attachmentsUrls);
      } else {
        setFiles([]);
      }
    } else if (type === 'upload') {
      setFiles(attachments || []);
    }
  }, [attachmentsUrls, attachments, type]);

  if (!files) {
    return (
      <Stack gap={2}>
        <Skeleton animation="wave" variant="rounded" height="34px" />
      </Stack>
    );
  }

  return (
    <Stack style={containerStyle} className="attachments-form">
      <Button
        className="add-button"
        component="label"
        role={undefined}
        endIcon={<RiFolderUploadFill size={40} />}
        variant="text"
      >
        <Stack direction="column" flexGrow={1}>
          <Typography className="title">
            {t(translations.daily_care_log.shift_details.unplanned_tasks.add_files)}
          </Typography>
          <Typography className="max">
            {t(translations.daily_care_log.shift_details.unplanned_tasks.files_size, {
              size: fileSizeMbLimit,
            })}
          </Typography>
        </Stack>

        <input
          type="file"
          accept={acceptedTypes.join(',')}
          multiple
          onChange={(event) => {
            if (event.target.files) {
              let inputFiles = Array.from(event.target.files);
              const transformedFiles: SavingAttachment[] = [];
              inputFiles.forEach((file) => {
                const types = getStorageAndAttachmentTypes(file.type);

                if (file.size < fileSizeLimit && types !== null) {
                  transformedFiles.push({
                    attachmentType: types.attachmentType,
                    storageType: types.storageType,
                    file,
                  });
                } else {
                  const error =
                    file.size >= fileSizeLimit
                      ? t(
                          translations.daily_care_log.shift_details.unplanned_tasks.file_size_error,
                          {size: fileSizeMbLimit}
                        )
                      : types === null
                        ? t(
                            translations.daily_care_log.shift_details.unplanned_tasks
                              .file_not_supported_error
                          )
                        : t(
                            translations.daily_care_log.shift_details.unplanned_tasks
                              .file_both_errors,
                            {size: fileSizeMbLimit}
                          );
                  const newErrors = filesErrors ? [...filesErrors, {file, error}] : [{file, error}];
                  onError(newErrors);
                  setFileErrors(newErrors);
                }
              });
              if (transformedFiles.length > 0) {
                const newFiles = files ? [...files, ...transformedFiles] : transformedFiles;
                setFiles(newFiles);
                onAttachmentsChange(newFiles);
              }
            }
          }}
        />
      </Button>
      {files?.map((file, index) => {
        const size = file.file.size;
        const sizeInMb = bytesToMegabytes(size);
        return (
          <Stack
            key={`${index}-${file.file.name.substring(0, file.file.name.indexOf('.'))}`}
            flexDirection="row"
            gap="5px"
            className="files-list"
          >
            <IoDocumentTextOutline size="18px" />
            <Stack className="size" flexGrow={1}>
              <Typography className="size">{file.file.name}</Typography>
              <Typography>
                {sizeInMb >= 1 ? `${sizeInMb.toFixed(1)} MB` : `${bytesToKilobytes(size)}KB`}
              </Typography>
            </Stack>
            <Button variant="text" component="label" onClick={() => setIsDeleteModalOpen(file)}>
              <FiTrash2 size="18px" />
            </Button>
          </Stack>
        );
      })}
      {filesErrors.map((file) => {
        const size = file.file.size;
        const sizeInMb = bytesToMegabytes(size);
        return (
          <Stack key={file.file.name} flexDirection="row" gap="5px" className="files-list">
            <IoDocumentTextOutline size="18px" />
            <Stack flexGrow={1}>
              <Typography>{file.file.name}</Typography>
              <Typography className="size">
                {sizeInMb >= 1 ? `${sizeInMb.toFixed(1)} MB` : `${bytesToKilobytes(size)}KB`}
              </Typography>
              <Typography className="error">{file.error}</Typography>
            </Stack>
            <Button
              variant="text"
              component="label"
              onClick={() => {
                const newFiles = filesErrors.filter((item) => item.file !== file.file);
                onError(newFiles);
                setFileErrors(newFiles);
              }}
            >
              <FiTrash2 size="18px" />
            </Button>
          </Stack>
        );
      })}
      {isDeleteModalOpen && (
        <DailyCareLogModal
          open
          title={t(translations.daily_care_log.shift_details.unplanned_tasks.delete_attachment, {
            name: isDeleteModalOpen?.file.name,
          })}
          onClose={() => setIsDeleteModalOpen(undefined)}
          onConfirm={async () => {
            const newFiles = files?.filter((file) => file !== isDeleteModalOpen);
            setFiles(newFiles);
            onAttachmentsChange(newFiles);
            setIsDeleteModalOpen(undefined);
          }}
        />
      )}
    </Stack>
  );
};

const bytesToMegabytes = (bytes: number) => {
  return bytes / (1024 * 1024);
};

const bytesToKilobytes = (bytes: number) => {
  return Math.round(bytes / 1024);
};
